/**
 * Núcleo NÃO-VISUAL do console de Ordens de Serviço (M5).
 *
 * Regras de LEITURA da OS que mais de uma tela precisa (kanban, ficha,
 * alocação) vivem aqui — funções puras, sem React e sem `api`. O que este
 * módulo NÃO faz, de propósito:
 *
 *  - **não decide transição de estado**: a máquina do §6.5 é do backend
 *    (`serviceOrderRules.js`); a tela só oferece o que o contrato permite ao
 *    console (BACKLOG→READY e →CANCELED) e deixa o 409 ser o juiz;
 *  - **não calcula SLA**: o `sla` vem pronto no DTO (`execution_seconds` já
 *    com as pausas descontadas). Recontar aqui criaria uma segunda verdade
 *    sobre produtividade — a pior espécie de duplicação;
 *  - **não deriva a JORNADA nem o split da alocação** (quais pernas o pallet
 *    percorre e em quantas OSs isso cai): isso é `POST /allocation/preview`, e
 *    reimplementar a regra no navegador faria a tela prometer OSs diferentes
 *    das que o servidor geraria.
 */

/**
 * As 4 colunas do kanban (§8.1.5). `PAUSED` NÃO é coluna: ela mora na coluna
 * "Em execução" com o distintivo de pausa.
 *
 * Motivo: uma OS pausada continua ATRIBUÍDA — o operador está com a carga (ou
 * acabou de largá-la num endereço que ele bipou) e o SLA dela está correndo em
 * tempo total. Uma 5ª coluna esconderia o fato mais importante do quadro
 * ("tem trabalho parado com dono") num canto separado; dentro de "Em execução"
 * ela aparece exatamente onde o Supervisor procura por trabalho em andamento.
 */
export const SERVICE_ORDER_COLUMNS = Object.freeze(['BACKLOG', 'READY', 'IN_PROGRESS', 'DONE']);

export const SERVICE_ORDER_STATUS = Object.freeze({
    BACKLOG: 'BACKLOG',
    READY: 'READY',
    IN_PROGRESS: 'IN_PROGRESS',
    PAUSED: 'PAUSED',
    DONE: 'DONE',
    CANCELED: 'CANCELED',
});

/** Comportamento que nasce da máquina e espera decisão do Supervisor (§6.5). */
export const BEHAVIOR_REPLENISHMENT = 'REPLENISHMENT';

/** Os dois status que o PATCH do console aceita — o resto é do chão. */
export const CONSOLE_STATUS_READY = 'READY';
export const CONSOLE_STATUS_CANCELED = 'CANCELED';

/**
 * Os dois status em que uma OS pode NASCER (`POST /service-orders`): fila de
 * decisão ou já liberada. Espelha o `SERVICE_ORDER_CREATE_STATUSES` do backend
 * — uma OS não nasce em execução (IN_PROGRESS vem do aceite no chão), nem
 * concluída, nem cancelada. A criação avulsa oferece EXATAMENTE o que o wire
 * aceita, em vez de mostrar seis opções e deixar quatro virarem 400.
 */
export const SERVICE_ORDER_CREATE_STATUSES = Object.freeze([
    SERVICE_ORDER_STATUS.BACKLOG,
    SERVICE_ORDER_STATUS.READY,
]);

// ── Criação AVULSA: a bancada de itens ──────────────────────────────────────
// A saída de emergência do M5 (`POST /service-orders`): o Supervisor descreve
// os itens EXATAMENTE como quer executá-los. Aqui NÃO existe jornada, perna,
// cabeceira nem handoff — quem monta isso é o planejador da alocação
// (`POST /allocation/preview`). Estas funções só arrumam as linhas da tela e as
// traduzem para o wire; qualquer regra de rota que aparecesse neste arquivo
// seria uma segunda verdade sobre a mesma coisa.

let draftSequence = 0;

/** Uma linha da criação avulsa: as duas pontas por CÓDIGO e o LPN (opcional). */
export function CreateServiceOrderItemDraft(seed = {}) {
    draftSequence += 1;
    return {
        key: `so-item-${draftSequence}`,
        from_location_code: seed.from_location_code ?? '',
        to_location_code: seed.to_location_code ?? '',
        lpn_code: seed.lpn_code ?? '',
    };
}

/** Linha SEM NADA — a sobra de um "Adicionar item" que ninguém preencheu. */
export function IsBlankServiceOrderItemDraft(row) {
    return (
        !String(row?.from_location_code ?? '').trim() &&
        !String(row?.to_location_code ?? '').trim() &&
        !String(row?.lpn_code ?? '').trim()
    );
}

/**
 * Linha que o wire aceita: as DUAS pontas por código. O LPN é opcional de
 * propósito — item sem LPN é "leve o que estiver nesta posição", e é o bipe da
 * execução que cobra a realidade (§2.2), não o planejamento.
 */
export function IsCompleteServiceOrderItemDraft(row) {
    return Boolean(String(row?.from_location_code ?? '').trim()) && Boolean(String(row?.to_location_code ?? '').trim());
}

/**
 * Linhas → itens do wire, na ORDEM em que o Supervisor as montou.
 *
 * ⚠️ A ordem É a sequência de execução: o backend deriva a `seq` da posição no
 * array (aceitar `seq` no corpo abriria espaço para buraco e duplicata). Quem
 * reordenar esta lista está reordenando o trabalho do operador.
 */
export function ServiceOrderItemsFromDrafts(rows) {
    return (Array.isArray(rows) ? rows : []).filter(IsCompleteServiceOrderItemDraft).map((row) => {
        const lpnCode = String(row.lpn_code ?? '').trim();
        return {
            from_location_code: String(row.from_location_code).trim(),
            to_location_code: String(row.to_location_code).trim(),
            ...(lpnCode ? { lpn_code: lpnCode } : {}),
        };
    });
}

const COLUMN_OF_STATUS = {
    BACKLOG: 'BACKLOG',
    READY: 'READY',
    IN_PROGRESS: 'IN_PROGRESS',
    PAUSED: 'IN_PROGRESS',
    DONE: 'DONE',
    // CANCELED não tem coluna: ver `GroupServiceOrdersByColumn`.
};

/** Em qual coluna do quadro esta OS aparece — `null` = fora do quadro. */
export function ColumnOfServiceOrder(status) {
    return COLUMN_OF_STATUS[String(status || '').toUpperCase()] ?? null;
}

/**
 * Distribui as OSs pelas 4 colunas.
 *
 * **Canceladas ficam FORA por padrão.** O quadro é uma superfície de trabalho:
 * o que está lá é o que ainda vai acontecer ou acabou de acontecer. Cancelada
 * é material de auditoria — entra só quando o Supervisor liga o interruptor,
 * e mesmo aí entra na coluna "Concluídas" com distintivo próprio, porque
 * misturá-la em silêncio com trabalho concluído é a forma mais fácil de
 * inflar um indicador de produtividade sem ninguém perceber.
 */
export function GroupServiceOrdersByColumn(rows, { includeCanceled = false } = {}) {
    const grouped = Object.fromEntries(SERVICE_ORDER_COLUMNS.map((column) => [column, []]));
    for (const row of Array.isArray(rows) ? rows : []) {
        const status = String(row?.status || '').toUpperCase();
        if (status === SERVICE_ORDER_STATUS.CANCELED) {
            if (includeCanceled) {
                grouped.DONE.push(row);
            }
            continue;
        }
        const column = ColumnOfServiceOrder(status);
        if (column) {
            grouped[column].push(row);
        }
    }
    return grouped;
}

/** Cor do distintivo de status (a gramática de cor do Badge do app). */
export function ServiceOrderStatusVariant(status) {
    switch (String(status || '').toUpperCase()) {
        case SERVICE_ORDER_STATUS.DONE:
            return 'success';
        case SERVICE_ORDER_STATUS.IN_PROGRESS:
            return 'info';
        case SERVICE_ORDER_STATUS.PAUSED:
            return 'warning';
        case SERVICE_ORDER_STATUS.CANCELED:
            return 'danger';
        case SERVICE_ORDER_STATUS.READY:
            return 'neutral';
        default:
            return 'default';
    }
}

/** Cor do distintivo do ITEM (pulado é alerta, não erro: a rota seguiu). */
export function ServiceOrderItemStatusVariant(status) {
    switch (String(status || '').toUpperCase()) {
        case 'DONE':
            return 'success';
        case 'EXECUTING':
            return 'info';
        case 'SKIPPED_ALERT':
            return 'warning';
        default:
            return 'default';
    }
}

/**
 * O TEMPO da OS, com o rótulo honesto de cada caso.
 *
 * Devolve `{seconds, running, interrupted, paused_seconds, open_pause_count}`:
 *
 *  - `interrupted: true` quando a OS foi CANCELADA. O backend carimba
 *    `finished_at` no cancelamento (senão o número correria para sempre no
 *    painel), mas esse intervalo **não é tempo de execução de trabalho
 *    concluído** — quem consumir isto tem de rotular como interrompido, nunca
 *    com "Tempo de execução" (decisão registrada na onda A do M5);
 *  - `running: true` enquanto a OS não terminou — o número ainda cresce;
 *  - `seconds` é sempre o `execution_seconds` do backend (pausas já
 *    descontadas), nunca uma conta refeita aqui.
 */
export function ServiceOrderTiming(row) {
    const sla = row?.sla ?? null;
    const status = String(row?.status || '').toUpperCase();
    return {
        accepted: Boolean(sla?.accepted),
        seconds: sla?.execution_seconds ?? null,
        elapsed_seconds: sla?.elapsed_seconds ?? null,
        paused_seconds: sla?.paused_seconds ?? null,
        pause_count: Number(sla?.pause_count ?? 0),
        open_pause_count: Number(sla?.open_pause_count ?? 0),
        running: Boolean(sla?.running) && status !== SERVICE_ORDER_STATUS.CANCELED,
        interrupted: status === SERVICE_ORDER_STATUS.CANCELED && Boolean(sla?.accepted),
    };
}

/** Progresso de itens da OS — `done` inclui os pulados (a rota fechou neles). */
export function ServiceOrderProgress(row) {
    const total = Number(row?.items_count ?? 0);
    const done = Number(row?.items_done_count ?? 0);
    const skipped = Number(row?.items_skipped_count ?? 0);
    const closed = Math.min(total, done + skipped);
    return {
        total,
        done,
        skipped,
        closed,
        percent: total > 0 ? Math.round((closed / total) * 100) : 0,
    };
}

/**
 * A pausa ABERTA da OS (a que ainda não foi retomada), se houver. É ela que
 * explica por que uma OS "em execução" está parada, e há quanto tempo.
 */
export function OpenPauseOf(order) {
    const pauses = Array.isArray(order?.pauses) ? order.pauses : [];
    return pauses.find((pause) => pause && !pause.resumed_at) ?? null;
}

/**
 * Duração de UMA pausa, em segundos. O DTO manda os dois carimbos e o SLA já
 * somado; o intervalo de cada linha da lista de pausas não vem pronto, e é a
 * única conta de tempo que esta camada faz — não é o SLA (esse continua
 * chegando calculado do backend), é a leitura de um intervalo exibido.
 * Pausa ABERTA conta até agora, com a mesma semântica do `ComputeServiceOrderSla`.
 */
export function PauseDurationSeconds(pause, { now = null } = {}) {
    const start = pause?.paused_at ? new Date(pause.paused_at).getTime() : Number.NaN;
    if (!Number.isFinite(start)) {
        return null;
    }
    const end = pause?.resumed_at ? new Date(pause.resumed_at).getTime() : (now ?? Date.now());
    if (!Number.isFinite(end)) {
        return null;
    }
    return Math.max(0, Math.round((end - start) / 1000));
}

/**
 * Índice `id → item` de TODAS as OSs de uma jornada (a desta ficha + as irmãs
 * do grupo), com a OS de origem junto.
 *
 * ⚠️ Substitui o `PairItemsBySeq`, que MORREU em 11/08 com o modelo da jornada
 * (M5 r1, regra 4). "Mesma `seq` na OS irmã" deixou de identificar o par: uma
 * jornada pode pôr DUAS pernas verticais na mesma OS, então a `seq` voltou a
 * ser o que o nome diz — a ordem do item DENTRO da sua OS. Casar por `seq`
 * hoje emparelharia itens ERRADOS, mostrando ao Supervisor um operador
 * esperando outro que não tem nada a ver com ele.
 *
 * O vínculo agora é a coluna `handoff_from_item_id`: o item que RECEBE aponta
 * para o item que ENTREGA o pallet no endereço onde ele começa.
 */
export function IndexServiceOrderItemsById(orders) {
    const byId = new Map();
    for (const order of Array.isArray(orders) ? orders : []) {
        for (const item of Array.isArray(order?.items) ? order.items : []) {
            if (item?.id) {
                byId.set(String(item.id), { item, order });
            }
        }
    }
    return byId;
}

/**
 * Quem ENTREGA o pallet para este item — `{item, order}` ou `null` quando não
 * há espera (primeira perna da jornada, ou perna seguinte na MESMA OS, onde o
 * operador já está com a carga na mão).
 */
export function HandoffSourceOf(item, itemsById) {
    const sourceId = item?.handoff_from_item_id;
    return sourceId ? (itemsById?.get(String(sourceId)) ?? null) : null;
}

/**
 * O item está ESPERANDO a entrega? A trava é do backend
 * (`WMS26_SERVICE_ORDER_PAIR_WAIT`, 409 no bipe); aqui é só leitura, para o
 * Supervisor entender uma fila parada: um item ainda pendente cujo
 * ENTREGADOR não concluiu está aguardando o pallet chegar.
 *
 * Par PULADO (`SKIPPED_ALERT`) NÃO é espera: o pallet não chegou e não vai
 * chegar — chamar isso de "aguardando" mandaria o Supervisor esperar por nada.
 */
export function ItemIsWaitingForHandoff(item, handoffItem) {
    if (!handoffItem) {
        return false;
    }
    const status = String(item?.status || '').toUpperCase();
    const sourceStatus = String(handoffItem?.status || '').toUpperCase();
    return status === 'PENDING' && sourceStatus !== 'DONE' && sourceStatus !== 'SKIPPED_ALERT';
}

/**
 * Uma ponta do item no shape que os utilitários de endereço esperam
 * (`{code, type, rack, bay, level, position}`). O DTO traz as colunas com
 * prefixo `from_location_` / `to_location_` — `_rack` é a LETRA do rack e
 * `_block` o nº do bloco (vão), que aqui vira `bay`; a conversão fica em UM
 * lugar, como `IncidentLocationOf` já faz para o incidente — assim ficha,
 * cartão e alocação nunca discordam sobre como falar o mesmo endereço.
 */
export function ItemLocationOf(item, side) {
    const code = item?.[`${side}_location_code`];
    if (!code) {
        return null;
    }
    return {
        code,
        type: item[`${side}_location_type`],
        rack: item[`${side}_location_rack`],
        bay: item[`${side}_location_block`],
        level: item[`${side}_location_level`],
        position: item[`${side}_location_position`],
    };
}

/** Códigos das posições de incidente (`INC-*`) — a raia do §8.1.5. */
export const INCIDENT_LOCATION_TYPE = 'INCIDENT';

/**
 * Itens da OS no formato que o comparador de rotas consome
 * (`POST /routes/compare`): id (vira a chave da sequência manual), seq e as
 * duas pontas por CÓDIGO — a mesma gramática do wire de execução.
 */
export function RouteItemsOfOrder(order) {
    const items = Array.isArray(order?.items) ? order.items : [];
    return items
        .filter((item) => item?.from_location_code && item?.to_location_code)
        .map((item) => ({
            id: item.id,
            seq: item.seq,
            lpn_code: item.lpn_code ?? null,
            from_location_code: item.from_location_code,
            to_location_code: item.to_location_code,
        }));
}
